import React, { useState } from 'react'
import moment from 'moment'
import { SingleDatePicker, CalendarDay } from 'react-dates'
import { useTranslation } from 'react-i18next'
import { parseSalesDay } from '@/libs/post-closing-sales-adjustment'

type DatePickerProps = {
  date: moment.Moment | null
  setDate: React.Dispatch<React.SetStateAction<moment.Moment | null>>
  confirmedDay: string
  inputId?: string
  /** 締め後修正など: 締め済み日も選択可能（未指定時は締め済み日を選択不可） */
  allowClosedDates?: boolean
  withPortal?: boolean
  enableOutsideDays?: boolean
  initialVisibleMonth?: (() => moment.Moment) | null
}

export const DatePicker: React.FC<DatePickerProps> = ({
  date,
  setDate,
  confirmedDay,
  inputId = 'date',
  allowClosedDates = false,
  withPortal = false,
  enableOutsideDays = true,
  initialVisibleMonth,
}) => {
  const { t } = useTranslation()
  const [calendarFocused, setCalendarFocused] = useState<boolean>(false)
  const confirmedMoment = parseSalesDay(confirmedDay)
  const hasValidConfirmedDay = confirmedMoment.isValid()

  // 選択可否と網掛けを同一述語から導出（境界・不正値時の fail 方向を揃える）
  const isClosedDay = (day: moment.Moment) => hasValidConfirmedDay && !day.isAfter(confirmedMoment, 'day')
  const isOutsideRange = (day: moment.Moment) => !allowClosedDates && isClosedDay(day)
  const isClosedSelectableDay = (day: moment.Moment | null) => allowClosedDates && !!day && isClosedDay(day)

  return (
    <>
      <SingleDatePicker
        date={date}
        onDateChange={changedDate => changedDate && setDate(changedDate)}
        focused={calendarFocused}
        onFocusChange={v => setCalendarFocused(Boolean(v.focused))}
        numberOfMonths={1}
        isOutsideRange={isOutsideRange}
        isDayHighlighted={isOutsideRange}
        enableOutsideDays={enableOutsideDays}
        withPortal={withPortal}
        initialVisibleMonth={initialVisibleMonth ?? undefined}
        displayFormat={t('MM-DD')}
        monthFormat={t('YYYY-MM')}
        id={inputId}
        hideKeyboardShortcutsPanel={true}
        calendarInfoPosition="bottom"
        renderCalendarInfo={
          allowClosedDates
            ? () => (
                <div className="closed-date-legend">
                  <span aria-hidden className="closed-date-legend__swatch" />
                  <span className="closed-date-legend__text">{t('HotelPms.PostClosing.closedDateHatchLegend')}</span>
                </div>
              )
            : undefined
        }
        navPrev={
          <div className="monthPrev">
            <img
              alt="arrow"
              src={require('@/static/images/button_arrow_left.svg')}
              onMouseOver={e => (e.currentTarget.src = require('@/static/images/button_arrow_left.svg'))}
              onMouseOut={e => (e.currentTarget.src = require('@/static/images/button_arrow_left.svg'))}
            />
          </div>
        }
        navNext={
          <div className="monthNext">
            <img
              alt="arrow"
              src={require('@/static/images/button_arrow_right.svg')}
              onMouseOver={e => (e.currentTarget.src = require('@/static/images/button_arrow_right.svg'))}
              onMouseOut={e => (e.currentTarget.src = require('@/static/images/button_arrow_right.svg'))}
            />
          </div>
        }
        renderCalendarDay={props => (
          <CalendarDay
            {...props}
            renderDayContents={day => {
              const closedSelectable = isClosedSelectableDay(day)
              return (
                <div className={closedSelectable ? 'day day--closed-selectable' : 'day'}>
                  {closedSelectable && <span aria-hidden className="day__closed-hatch" />}
                  <span className="day__label">{day?.format('D')}</span>
                </div>
              )
            }}
          />
        )}
      />
    </>
  )
}
